import type { ReactNode } from "react";
import {
  TimerReset,
  Mail,
  PackageOpen,
  CalendarClock,
  ClipboardCheck,
  Receipt,
  ShieldCheck,
  Calendar1,
  User,
  Wrench,
  Ban,
} from "lucide-react";

type PolicySummaryItemEntry = {
  type: "item";
  icon: ReactNode;
  content: ReactNode;
};

type PolicySummarySeparatorEntry = {
  type: "separator";
};

export type PolicySummaryEntry =
  PolicySummaryItemEntry | PolicySummarySeparatorEntry;

const iconSize = 16;
const sectionLinkClassName = "underline";

const sectionLink = (sectionNumber: number) => (
  <a href={`#section-${sectionNumber}`} className={sectionLinkClassName}>
    Section {sectionNumber}
  </a>
);

export const returnPolicySummaryItems: PolicySummaryEntry[] = [
  {
    type: "item",
    icon: <TimerReset size={iconSize} />,
    content:
      "You can return your Matic up to 6 months after purchase for a full refund.",
  },
  {
    type: "item",
    icon: <Mail size={iconSize} />,
    content: (
      <>
        Email{" "}
        <a href="mailto:support@maticrobots.com" className="underline">
          support@maticrobots.com
        </a>{" "}
        to get a prepaid label + return authorization (RMA).
      </>
    ),
  },
  {
    type: "item",
    icon: <PackageOpen size={iconSize} />,
    content: (
      <>
        Carefully pack up Matic according to these{" "}
        <a
          href="https://support.maticrobots.com/how-to-return-your-matic"
          className="underline"
        >
          return instructions.
        </a>
      </>
    ),
  },
  {
    type: "item",
    icon: <CalendarClock size={iconSize} />,
    content: "Ship within 14 days of receiving your label.",
  },
  {
    type: "item",
    icon: <ClipboardCheck size={iconSize} />,
    content:
      "Refunds go back to your original payment method after inspection (typically 10\u201315 business days).",
  },
  {
    type: "item",
    icon: <Receipt size={iconSize} />,
    content: "Orders without proof of purchase aren\u2019t eligible.",
  },
];

export const warrantySummaryItems: PolicySummaryEntry[] = [
  {
    type: "item",
    icon: <ShieldCheck size={iconSize} />,
    content: (
      <>
        <strong>Coverage:</strong> warranty covers Matic robot and charging dock
        against defects in materials and workmanship ({sectionLink(1)}). If you
        have Matic Care, your warranty also includes accidental damage coverage
        ({sectionLink(1)}, {sectionLink(5)}).
      </>
    ),
  },
  {
    type: "item",
    icon: <Calendar1 size={iconSize} />,
    content: (
      <>
        <strong>Term:</strong> 1 year from purchase (proof of purchase
        required). If you have Matic Care, your warranty coverage extends to 2
        years from the date of purchase ({sectionLink(2)}).
      </>
    ),
  },
  {
    type: "item",
    icon: <User size={iconSize} />,
    content: (
      <>
        <strong>Who{"\u2019"}s covered:</strong> original purchaser or gift
        recipient ({sectionLink(1)}).
      </>
    ),
  },
  {
    type: "item",
    icon: <Wrench size={iconSize} />,
    content: (
      <>
        <strong>The fix:</strong> Matic will repair, replace, or refund the unit
        at its discretion ({sectionLink(3)}).
      </>
    ),
  },
  {
    type: "item",
    icon: <Mail size={iconSize} />,
    content: (
      <>
        <strong>How to claim:</strong> email{" "}
        <a href="mailto:support@maticrobots.com" className="underline">
          support@maticrobots.com
        </a>{" "}
        to get the process started ({sectionLink(4)}).
      </>
    ),
  },
  { type: "separator" },
  {
    type: "item",
    icon: <Ban size={iconSize} />,
    content: (
      <>
        <strong>What{"\u2019"}s not covered:</strong> consumables, software,
        normal wear and tear, cosmetic damage, misuse, accidents, unauthorized
        repairs, non-Matic parts, refurbished products, or use outside the USA (
        {sectionLink(5)}).
      </>
    ),
  },
];
